/**
 * DeclareEnumLookupFields — the `lookup`-mode extras inside
 * `DeclareEnumColumnDialog`: the shared "Label column" dropdown, the reasons
 * Confirm may be blocked, and the escape hatch back to plain sampling.
 *
 * Split out of the dialog so the dialog stays a readable single screen; it owns
 * no state — the host (`GlobalDeclareEnumColumnDialog`) resolves the label
 * column and persists it.
 */

import { Checkbox } from '@/components/ui/checkbox';
import { LooseFkDisplayColumnSelect } from './LooseFkDisplayColumnSelect';

export type DeclareEnumLookupFieldsProps = {
  /** `schema.table` of the referenced table (schemaless engines: just the table). */
  qualifiedTarget: string;
  targetSchema: string;
  targetTable: string;
  /** SearchableSelect value: `''`, `'__none__'`, or a column name. */
  labelColumnValue: string;
  onLabelColumnChange: (next: string) => void;
  labelColumnOptions: ReadonlyArray<{ value: string; label: string }>;
  labelColumnLoading?: boolean;
  /** True when no label column is resolved / picked — Confirm is blocked. */
  missingLabelColumn: boolean;
  /** True when the referenced table blew past the 200-row cap — Confirm is blocked. */
  truncated: boolean;
  /** Flip to the plain sampled flow (distinct values off this column instead). */
  onUseSampledInstead: () => void;
  /**
   * "Mark <target> as a reference table" — confirm writes the table-level flag
   * instead of a declaration on this column, and EVERY column referencing that
   * table (this one included) becomes an implicit lookup enum.
   */
  referenceTable: boolean;
  onReferenceTableChange: (checked: boolean) => void;
};

export function DeclareEnumLookupFields({
  qualifiedTarget,
  targetSchema,
  targetTable,
  labelColumnValue,
  onLabelColumnChange,
  labelColumnOptions,
  labelColumnLoading,
  missingLabelColumn,
  truncated,
  onUseSampledInstead,
  referenceTable,
  onReferenceTableChange,
}: DeclareEnumLookupFieldsProps) {
  return (
    <div className="space-y-2" data-testid="declare-enum-lookup-fields">
      <LooseFkDisplayColumnSelect
        value={labelColumnValue}
        onChange={onLabelColumnChange}
        columnOptions={labelColumnOptions}
        loading={labelColumnLoading}
        targetSchema={targetSchema}
        targetTable={targetTable}
      />

      {truncated ? (
        <p
          className="text-xs text-destructive"
          data-testid="declare-enum-lookup-truncated"
        >
          {/* Too big to CACHE is not too big to LABEL. Before on-demand mode
              existed this line could only point at the row picker; now the
              block names the route that actually gives this user what they
              came for — names in the grid — and keeps the picker as the
              filtering answer it always was. */}
          <span className="font-mono" translate="no">
            {qualifiedTarget}
          </span>{' '}
          has 200+ rows — too many to cache as an enum. You can still show
          names: open any FK cell&apos;s popover and choose &ldquo;Show names
          from{' '}
          <span className="font-mono" translate="no">
            {qualifiedTarget}
          </span>
          &rdquo; (resolved on demand), or use the row picker for filtering.
        </p>
      ) : missingLabelColumn ? (
        <p
          className="text-xs text-muted-foreground"
          data-testid="declare-enum-lookup-needs-label"
        >
          Pick a label column to show names from{' '}
          <span className="font-mono" translate="no">
            {qualifiedTarget}
          </span>
          .
        </p>
      ) : null}

      {/* The inverse mental model: declare the SOURCE once (this column), or
          declare the TARGET once and let every column that references it show
          names. Sits under the label column because the label column is what
          the flag makes shared — it is stored per target table either way. */}
      <div className="flex items-start gap-2" data-testid="declare-enum-reference-table-row">
        <Checkbox
          id="declare-enum-reference-table"
          checked={referenceTable}
          onCheckedChange={(next) => onReferenceTableChange(next === true)}
          className="mt-0.5"
          data-testid="declare-enum-reference-table"
        />
        <div className="flex flex-col gap-0.5 min-w-0">
          <label
            htmlFor="declare-enum-reference-table"
            className="text-[11px] leading-snug cursor-pointer"
          >
            Mark{' '}
            <span className="font-mono" translate="no">
              {qualifiedTarget}
            </span>{' '}
            as a reference table — every column referencing it shows names
          </label>
          <p className="text-[10px] text-muted-foreground/80 leading-snug">
            Nothing is stored on this column; remove the flag and every one of
            them stops showing names.
          </p>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Want the distinct values on this column instead?{' '}
        <button
          type="button"
          data-testid="declare-enum-use-sampled"
          className="text-primary underline underline-offset-2 hover:no-underline"
          onClick={onUseSampledInstead}
        >
          Use sampled values instead
        </button>
      </p>
    </div>
  );
}
